import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { WishlistItem, WishlistUrgency, WishlistStatus, WishlistSourcingStatus } from '@/types';
import { track } from '@/lib/telemetry';

const MOCK_WISHLIST: WishlistItem[] = [
  {
    id: 'wl-1', dealership_id: 'demo-dealership', rep_id: 'rep-alex', rep_name: 'Alex Carter',
    customer_name: 'Sarah Chen', customer_phone: '(519) 555-0123', customer_email: null,
    deal_id: null, make: 'Toyota', model: 'Tacoma TRD', year_min: 2022, year_max: null,
    max_price: 42000, color: 'White', notes: 'Low miles preferred',
    urgency: 'hot', status: 'active', sourcing_status: 'searching',
    created_at: new Date(Date.now() - 2 * 86400000).toISOString(),
    updated_at: new Date(Date.now() - 2 * 86400000).toISOString(),
  },
  {
    id: 'wl-2', dealership_id: 'demo-dealership', rep_id: 'rep-alex', rep_name: 'Alex Carter',
    customer_name: 'Marcus Webb', customer_phone: '(519) 555-0145', customer_email: null,
    deal_id: null, make: 'Ford', model: 'Bronco Wildtrak', year_min: 2023, year_max: null,
    max_price: 58000, color: null, notes: 'Black exterior, 4-door',
    urgency: 'warm', status: 'active', sourcing_status: 'incoming',
    created_at: new Date(Date.now() - 6 * 86400000).toISOString(),
    updated_at: new Date(Date.now() - 6 * 86400000).toISOString(),
  },
  {
    id: 'wl-3', dealership_id: 'demo-dealership', rep_id: 'rep-maria', rep_name: 'Maria Lopez',
    customer_name: 'Priya Patel', customer_phone: null, customer_email: 'priya@example.com',
    deal_id: null, make: 'Honda', model: 'Pilot Elite', year_min: 2021, year_max: 2023,
    max_price: 45000, color: 'Black', notes: null,
    urgency: 'warm', status: 'active', sourcing_status: 'searching',
    created_at: new Date(Date.now() - 12 * 86400000).toISOString(),
    updated_at: new Date(Date.now() - 12 * 86400000).toISOString(),
  },
  {
    id: 'wl-4', dealership_id: 'demo-dealership', rep_id: 'rep-maria', rep_name: 'Maria Lopez',
    customer_name: 'Diego Alvarez', customer_phone: '(519) 555-0167', customer_email: null,
    deal_id: null, make: 'Hyundai', model: 'Palisade Calligraphy', year_min: null, year_max: null,
    max_price: null, color: 'Grey', notes: 'Trade-in for his 2018 Sonata',
    urgency: 'cold', status: 'active', sourcing_status: 'located',
    created_at: new Date(Date.now() - 25 * 86400000).toISOString(),
    updated_at: new Date(Date.now() - 25 * 86400000).toISOString(),
  },
  {
    id: 'wl-5', dealership_id: 'demo-dealership', rep_id: 'rep-ryan', rep_name: 'Ryan Mitchell',
    customer_name: 'Hannah Liu', customer_phone: '(519) 555-0188', customer_email: null,
    deal_id: null, make: 'Chevrolet', model: 'Tahoe LT', year_min: 2022, year_max: null,
    max_price: 62000, color: null, notes: '7-seater, family',
    urgency: 'hot', status: 'active', sourcing_status: 'notified',
    created_at: new Date(Date.now() - 1 * 86400000).toISOString(),
    updated_at: new Date(Date.now() - 1 * 86400000).toISOString(),
  },
  {
    id: 'wl-6', dealership_id: 'demo-dealership', rep_id: 'rep-sam', rep_name: 'Samantha Reed',
    customer_name: 'Wei Zhang', customer_phone: '(519) 555-0199', customer_email: null,
    deal_id: null, make: 'Nissan', model: 'Pathfinder Platinum', year_min: 2021, year_max: 2022,
    max_price: 41000, color: 'Blue', notes: null,
    urgency: 'warm', status: 'active', sourcing_status: 'appointment_set',
    created_at: new Date(Date.now() - 8 * 86400000).toISOString(),
    updated_at: new Date(Date.now() - 8 * 86400000).toISOString(),
  },
];

export interface WishlistInput {
  customer_name: string;
  customer_phone?: string | null;
  customer_email?: string | null;
  deal_id?: string | null;
  make: string;
  model?: string | null;
  year_min?: number | null;
  year_max?: number | null;
  max_price?: number | null;
  color?: string | null;
  notes?: string | null;
  urgency?: WishlistUrgency;
  sourcing_status?: WishlistSourcingStatus;
}

export function useWishlist() {
  const { mode, dealershipId, userId } = useAuth();
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (mode === 'demo') {
      setItems(MOCK_WISHLIST);
      setLoading(false);
      setError(null);
      return;
    }
    if (mode !== 'real' || !dealershipId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('wishlist_items')
      .select('*')
      .eq('dealership_id', dealershipId)
      .order('created_at', { ascending: false });
    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }
    const rows = (data ?? []) as WishlistItem[];
    // Resolve rep names
    const repIds = Array.from(new Set(rows.map(r => r.rep_id).filter(Boolean)));
    let nameMap = new Map<string, string>();
    if (repIds.length > 0) {
      const { data: profs } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', repIds);
      (profs ?? []).forEach((p: any) => nameMap.set(p.id, p.full_name || p.email || 'Rep'));
    }
    setItems(rows.map(r => ({ ...r, rep_name: nameMap.get(r.rep_id) || 'Rep' })));
    setError(null);
    setLoading(false);
  }, [mode, dealershipId]);

  useEffect(() => { load(); }, [load]);

  // Realtime
  useEffect(() => {
    if (mode !== 'real' || !dealershipId) return;
    const channel = supabase
      .channel(`wishlist:${dealershipId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'wishlist_items',
        filter: `dealership_id=eq.${dealershipId}`,
      }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [mode, dealershipId, load]);

  const create = useCallback(async (input: WishlistInput) => {
    if (mode !== 'real' || !dealershipId || !userId) {
      throw new Error('Sign in to a real dealership to save wishlists');
    }
    const { error } = await supabase.from('wishlist_items').insert({
      dealership_id: dealershipId,
      rep_id: userId,
      customer_name: input.customer_name.trim(),
      customer_phone: input.customer_phone?.trim() || null,
      customer_email: input.customer_email?.trim() || null,
      deal_id: input.deal_id ?? null,
      make: input.make.trim(),
      model: input.model?.trim() || null,
      year_min: input.year_min ?? null,
      year_max: input.year_max ?? null,
      max_price: input.max_price ?? null,
      color: input.color?.trim() || null,
      notes: input.notes?.trim() || null,
      urgency: input.urgency ?? 'warm',
      status: 'active',
      sourcing_status: input.sourcing_status ?? 'searching',
    });
    if (error) throw error;
    await load();
    track('wishlist_created', { userId, dealershipId, mode }, { make: input.make, urgency: input.urgency ?? 'warm' });
  }, [mode, dealershipId, userId, load]);

  const update = useCallback(async (id: string, patch: Partial<WishlistInput> & { status?: WishlistStatus }) => {
    if (mode !== 'real') throw new Error('Sign in to a real dealership to update wishlists');
    const cleaned: any = {};
    Object.entries(patch).forEach(([k, v]) => {
      if (v === undefined) return;
      cleaned[k] = typeof v === 'string' ? (v.trim() || null) : v;
    });
    const { error } = await supabase.from('wishlist_items').update(cleaned).eq('id', id);
    if (error) throw error;
    await load();
    if (cleaned.status) {
      track('wishlist_status_changed', { userId, dealershipId, mode }, { id, status: cleaned.status });
    }
  }, [mode, dealershipId, userId, load]);

  const remove = useCallback(async (id: string) => {
    if (mode !== 'real') throw new Error('Sign in to a real dealership to delete wishlists');
    const { error } = await supabase.from('wishlist_items').delete().eq('id', id);
    if (error) throw error;
    await load();
  }, [mode, load]);

  const touch = useCallback(async (id: string) => {
    if (mode !== 'real') throw new Error('Sign in to a real dealership');
    const { error } = await supabase
      .from('wishlist_items')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
    await load();
  }, [mode, load]);

  const convertToDeal = useCallback(async (item: WishlistItem): Promise<string> => {
    if (mode !== 'real' || !dealershipId || !userId) {
      throw new Error('Sign in to a real dealership to convert wishlists');
    }
    const vehicle = [item.year_min, item.color, item.make, item.model]
      .filter(Boolean).join(' ').trim() || item.make;
    const { data, error } = await supabase
      .from('deals')
      .insert({
        dealership_id: dealershipId,
        rep_id: item.rep_id ?? userId,
        customer_name: item.customer_name,
        phone: item.customer_phone,
        email: item.customer_email,
        vehicle,
        stage: 'new',
        priority: item.urgency === 'hot' ? 'hot' : item.urgency === 'cold' ? 'cold' : 'warm',
        estimated_value: item.max_price ?? 0,
        next_step: 'follow_up',
        notes: item.notes ?? '',
      })
      .select('id')
      .single();
    if (error) throw error;
    await supabase.from('wishlist_items')
      .update({ status: 'fulfilled', deal_id: data.id })
      .eq('id', item.id);
    await load();
    return data.id as string;
  }, [mode, dealershipId, userId, load]);

  return { items, loading, error, refresh: load, create, update, remove, touch, convertToDeal };
}